import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { User as AppUser } from '../types';
import supabase from '../lib/supabase';
import { User as SupabaseUser } from '@supabase/supabase-js';
// Input sanitization helpers
const sanitizeInput = {
  // Sanitize email input
  email: (input: string): string => {
    return input.toLowerCase().trim().replace(/[^a-z0-9@._-]/g, '');
  },

  // Validate and sanitize name input
  name: (input: string): string => {
    return input.trim().replace(/[^a-zA-Z0-9\s\-_.]/g, '');
  }
};

interface AuthContextType {
  user: AppUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  signUp: (email: string, password: string, name: string, language: string) => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  updateProfile: (name: string, language: string) => Promise<void>;
  deleteAccount: () => Promise<void>;
  error: string | null;
  isEmailVerified: boolean;
  refreshSession: () => Promise<void>;
}

const initialContext: AuthContextType = {
  user: null,
  isAuthenticated: false,
  isLoading: true,
  login: async () => {},
  logout: async () => {},
  signUp: async () => {},
  resetPassword: async () => {},
  updateProfile: async () => {},
  deleteAccount: async () => {},
  error: null,
  isEmailVerified: false,
  refreshSession: async () => {},
};

const AuthContext = createContext<AuthContextType>(initialContext);
export const useAuth = () => useContext(AuthContext);

function mapSupabaseUser(user: SupabaseUser | null): AppUser | null {
  if (!user) return null;
  return {
    id: user.id,
    name: user.user_metadata?.full_name || user.email || '',
    email: user.email || '',
    avatar: user.user_metadata?.avatar_url || undefined,
    user_metadata: user.user_metadata || {},
  };
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AppUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEmailVerified, setIsEmailVerified] = useState(false);

  // Token refresh management
  const tokenRefreshRef = useRef<NodeJS.Timeout | null>(null);

  // Helper to check email verification
  const checkEmailVerified = useCallback((supabaseUser: SupabaseUser | null) => {
    if (!supabaseUser) return false;
    // Supabase marks email_confirmed_at if verified
    return !!supabaseUser.email_confirmed_at;
  }, []);

  // Refresh session token
  const refreshSession = useCallback(async () => {
    try {
      const { data, error } = await supabase.auth.refreshSession();
      if (error) {
        console.error('Token refresh failed:', error);
        await logout();
        return;
      }
      
      if (data.session?.user) {
        setUser(mapSupabaseUser(data.session.user));
        setIsEmailVerified(checkEmailVerified(data.session.user));
        
        // Schedule next refresh (refresh 5 minutes before expiration)
        const expiresIn = data.session.expires_in || 3600;
        const refreshIn = (expiresIn - 300) * 1000; // 5 minutes before expiry
        
        if (tokenRefreshRef.current) {
          clearTimeout(tokenRefreshRef.current);
        }
        
        tokenRefreshRef.current = setTimeout(refreshSession, refreshIn);
      }
    } catch (err) {
      console.error('Session refresh error:', err);
      await logout();
    }
  }, [checkEmailVerified]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (tokenRefreshRef.current) {
        clearTimeout(tokenRefreshRef.current);
      }
    };
  }, []);

  // Listen for auth state changes and set user/session
  useEffect(() => {
    const setSession = async () => {
      setIsLoading(true);
      const { data, error } = await supabase.auth.getSession();
      if (error) {
        setUser(null);
        setIsEmailVerified(false);
        setIsLoading(false);
        return;
      }
      const session = data.session;
      if (session?.user) {
        setUser(mapSupabaseUser(session.user));
        setIsEmailVerified(checkEmailVerified(session.user));
        
        // Schedule token refresh
        const expiresIn = session.expires_in || 3600;
        const refreshIn = (expiresIn - 300) * 1000; // 5 minutes before expiry
        
        if (tokenRefreshRef.current) {
          clearTimeout(tokenRefreshRef.current);
        }
        
        tokenRefreshRef.current = setTimeout(refreshSession, refreshIn);
      } else {
        setUser(null);
        setIsEmailVerified(false);
      }
      setIsLoading(false);
    };
    setSession();
    const { data: listener } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.user) {
        setUser(mapSupabaseUser(session.user));
        setIsEmailVerified(checkEmailVerified(session.user));
      } else {
        setUser(null);
        setIsEmailVerified(false);
        
        // Clear timeouts when user logs out
        if (tokenRefreshRef.current) {
          clearTimeout(tokenRefreshRef.current);
        }
      }
    });
    return () => {
      listener.subscription.unsubscribe();
    };
  }, [checkEmailVerified, refreshSession]);

  const login = async (email: string, password: string) => {
    const sanitizedEmail = sanitizeInput.email(email);
    
    setIsLoading(true);
    setError(null);
    try {
      const { data, error } = await supabase.auth.signInWithPassword({ 
        email: sanitizedEmail, 
        password 
      });
      if (error) throw error;
      setUser(mapSupabaseUser(data.user));
      setIsEmailVerified(checkEmailVerified(data.user));
    } catch (err: any) {
      setError(err.message || 'An error occurred during login');
      setUser(null);
      setIsEmailVerified(false);
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    setIsLoading(true);
    setError(null);
    try {
      await supabase.auth.signOut();
      setUser(null);
      setIsEmailVerified(false);
      
      // Clear timeouts
      if (tokenRefreshRef.current) {
        clearTimeout(tokenRefreshRef.current);
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred during logout');
    } finally {
      setIsLoading(false);
    }
  };

  const signUp = async (email: string, password: string, name: string, language: string) => {
    const sanitizedEmail = sanitizeInput.email(email);
    const sanitizedName = sanitizeInput.name(name);
    
    setIsLoading(true);
    setError(null);
    try {
      const emailRedirectEnabled = process.env.REACT_APP_SUPABASE_EMAIL_REDIRECT_ENABLED === 'true';
      const emailRedirectUrl = process.env.REACT_APP_SUPABASE_EMAIL_REDIRECT_URL || (window.location.origin + '/verify');
      const options = emailRedirectEnabled ? { emailRedirectTo: emailRedirectUrl } : undefined;
      const { data, error } = await supabase.auth.signUp({
        email: sanitizedEmail,
        password,
        options: {
          data: {
            full_name: sanitizedName,
            language: language,
          },
          ...(options ? { emailRedirectTo: options.emailRedirectTo } : {}),
        },
      });
      if (error) throw error;
      setUser(mapSupabaseUser(data.user));
      setIsEmailVerified(false); // User must verify email
    } catch (err: any) {
      setError(err.message || 'An error occurred during sign up');
      setUser(null);
      setIsEmailVerified(false);
    } finally {
      setIsLoading(false);
    }
  };

  const resetPassword = async (email: string) => {
    const sanitizedEmail = sanitizeInput.email(email);
    
    setIsLoading(true);
    setError(null);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(sanitizedEmail, {
        redirectTo: window.location.origin + '/reset-password',
      });
      if (error) throw error;
    } catch (err: any) {
      setError(err.message || 'An error occurred during password reset');
    } finally {
      setIsLoading(false);
    }
  };

  const updateProfile = async (name: string, language: string) => {
    const sanitizedName = sanitizeInput.name(name);
    setIsLoading(true);
    setError(null);
    try {
      const { error } = await supabase.auth.updateUser({
        data: { full_name: sanitizedName, language }
      });
      if (error) throw error;
      setUser(prevUser => prevUser ? { ...prevUser, name: sanitizedName, user_metadata: { ...prevUser.user_metadata, language } } : null);
    } catch (err: any) {
      setError(err.message || 'An error occurred during profile update');
    } finally {
      setIsLoading(false);
    }
  };

  const deleteAccount = async () => {
    setIsLoading(true);
    setError(null);
    try {
      // Note: Account deletion may require admin privileges or a different approach
      // For now, we'll just sign out the user and show a message
      await supabase.auth.signOut();
      setUser(null);
      setIsEmailVerified(false);
      setError('Account deletion requested. Please contact support to complete the process.');
    } catch (err: any) {
      setError(err.message || 'An error occurred during account deletion');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        login,
        logout,
        signUp,
        resetPassword,
        updateProfile,
        deleteAccount,
        error,
        isEmailVerified,
        refreshSession,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};